import register from 'preact-custom-element';
import type { FunctionComponent } from 'preact';

import { getBoolProp } from '../../../utils/props';
import { UserDetailsView, type FieldDescriptor } from './UserDetailsView';

import '../../attribution/PoweredBySmileId';

declare global {
  interface WindowEventMap {
    'smileid-user-details.submitted': CustomEvent<Record<string, string>>;
  }
}

interface Props {
  'theme-color'?: string;
  'hide-attribution'?: string | boolean;
  // Field descriptors driving the form. Accepts a JSON string (attribute, how
  // embed sets it before insertion) or an array (property/tests). Omit to
  // render the default first/last name + contact set.
  fields?: string | FieldDescriptor[];
  // Pre-fill values keyed by field name — JSON string or object.
  values?: string | Record<string, string>;
  // Renders every input read-only and always enables Continue (demo mode /
  // `allow_modification: false` prefill), so a locked form is never a dead-end.
  'read-only'?: string | boolean;
  // Field names to render read-only while the rest stay editable (partial
  // partner prefill) — JSON string or array.
  'locked-fields'?: string | string[];
}

const parseJson = <T,>(raw: unknown): T | null => {
  if (raw == null) return null;
  if (typeof raw !== 'string') return raw as T;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed) as T;
  } catch {
    return null;
  }
};

/**
 * Standalone, product-agnostic "user details" screen. Hosted as its own custom
 * element with its own build entry (`@smileid/web-sdk/user-details`) so
 * any product (v3 SmartSelfie, DocV, Biometric KYC, …) can mount it without
 * pulling in the selfie/`smart-camera-web` bundle.
 *
 * Rendered only when the partner config omits `user_details` — a
 * partner-supplied value skips this screen entirely (see `smartselfie-auth.js`
 * / `biometric-kyc.js`).
 *
 * The field set is server-driven: pass `fields` (built from the v3 services
 * config) to render an arbitrary set, including `select` dropdowns; with no
 * `fields` the element renders the default first/last name + contact fields, so
 * existing SmartSelfie/DocV mounts are unaffected.
 *
 * Events:
 *   - `smileid-user-details.submitted` — detail = a map keyed by field name,
 *     e.g. `{ id_number?, given_names, last_name, email?, phone_number? }`.
 */
const UserDetails: FunctionComponent<Props> = ({
  'theme-color': themeColor = '#001096',
  'hide-attribution': hideAttributionProp = false,
  fields,
  values,
  'read-only': readOnlyProp = false,
  'locked-fields': lockedFieldsProp,
}) => {
  const hideAttribution = getBoolProp(hideAttributionProp);
  const readOnly = getBoolProp(readOnlyProp);
  const parsedFields = parseJson<FieldDescriptor[]>(fields);
  const parsedValues = parseJson<Record<string, string>>(values);
  const parsedLockedFields = parseJson<string[]>(lockedFieldsProp);

  return (
    <UserDetailsView
      themeColor={themeColor}
      hideAttribution={hideAttribution}
      fields={parsedFields}
      initialValues={parsedValues}
      readOnly={readOnly}
      lockedFields={parsedLockedFields}
      onSubmit={(details: Record<string, string>) => {
        window.dispatchEvent(
          new CustomEvent('smileid-user-details.submitted', {
            detail: details,
          }),
        );
      }}
    />
  );
};

if (!customElements.get('smileid-user-details')) {
  register(
    UserDetails,
    'smileid-user-details',
    [
      'theme-color',
      'hide-attribution',
      'fields',
      'values',
      'read-only',
      'locked-fields',
    ],
    { shadow: true },
  );
}

export default UserDetails;
